import { Button } from '@/ui-kit'
import { formatNumber } from '@/utils'
import { Trans } from '@lingui/react/macro'
import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  type MethodId,
  PAYPAL_CONSENT_CANCELLED_STATE,
  STRIPE_ONBOARDING_COMPLETE_STATE,
  peekFreshStash,
  resolveLowestMin,
  useFetchSettings,
  useFetchUserBalance
} from '../api'
import { WithdrawModal } from './WithdrawModal'

export const BalanceWidget = () => {
  const { data: balance } = useFetchUserBalance()
  const { data: settings, isLoading: isSettingsLoading } = useFetchSettings()
  const location = useLocation()
  const navigate = useNavigate()
  const minPayout = resolveLowestMin(settings)
  const minAmount = formatNumber(minPayout, 2)
  const canWithdraw = (balance?.availableUsd ?? 0) >= minPayout
  // The PayPal callback navigates here with this location.state flag when the user backed out of
  // the consent screen — the reopened modal shows a "sign-in was cancelled" notice.
  const consentCancelledNotice = Boolean(
    (location.state as Record<string, unknown> | null)?.[PAYPAL_CONSENT_CANCELLED_STATE]
  )
  const stripeOnboardingComplete = Boolean(
    (location.state as Record<string, unknown> | null)?.[STRIPE_ONBOARDING_COMPLETE_STATE]
  )

  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false)
  const [initialWithdrawMethod, setInitialWithdrawMethod] = useState<MethodId | undefined>()
  const onWithdrawClick = () => setIsWithdrawOpen(true)

  // Reopen the withdraw modal when a PayPal ceremony stash survived the consent redirect — the
  // modal consumes it on open and seeds the form back to where the user left off (IV5).
  useEffect(() => {
    if (peekFreshStash()) setIsWithdrawOpen(true)
  }, [])

  useEffect(() => {
    if (!stripeOnboardingComplete) return
    if (!settings) return
    if (!settings?.stripeConnectEnabled) {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
      return
    }
    setInitialWithdrawMethod('stripe-connect')
    setIsWithdrawOpen(true)
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null })
  }, [location.pathname, location.search, navigate, settings?.stripeConnectEnabled, stripeOnboardingComplete])

  const handleWithdrawOpenChange = (open: boolean) => {
    setIsWithdrawOpen(open)
    if (!open) setInitialWithdrawMethod(undefined)
  }

  return (
    <>
      <div className="mt-3 flex flex-col gap-4">
        <div className="flex flex-col border-b border-solid border-gray-100 pb-4">
          <div className="text-gray-90">
            <Trans>Balance</Trans>
          </div>
          <div className="text-h2 text-orange">{balance ? `$${formatNumber(balance.availableUsd, 2)}` : '$-.--'}</div>
        </div>
        <div className="max-mobile:pt-0 flex flex-col pt-4 pb-4">
          <Button onClick={onWithdrawClick} isDisabled={!canWithdraw} className="w-full">
            <Trans>Withdraw</Trans>
          </Button>
          {!isSettingsLoading && (
            <div className="text-body-3 text-gray-90 mt-2">
              <Trans>Min. withdrawal: ${minAmount}</Trans>
            </div>
          )}
        </div>
      </div>
      <WithdrawModal
        open={isWithdrawOpen}
        onOpenChange={handleWithdrawOpenChange}
        consentCancelledNotice={consentCancelledNotice}
        initialPaymentMethod={initialWithdrawMethod}
      />
    </>
  )
}
